/**
 * Public brand-asset CDN.
 *
 * Logos, favicons, OG images, and other shared brand artwork live on
 * `assets.areaadvertising.com` (Cloudflare R2 bucket). Hosting once and
 * referencing by absolute URL keeps the main site, the BIAB portal, and
 * the digital site all in sync — a logo update flips for everyone the
 * moment the file is replaced.
 *
 * Override the base via the `ASSETS_BASE_URL` env var (useful for
 * staging buckets or local proxies).
 */

export const ASSETS_BASE = (
  process.env.ASSETS_BASE_URL ?? "https://assets.areaadvertising.com"
).replace(/\/$/, "");

/**
 * Bump this when a brand asset is replaced in R2 so Next/Image's optimized
 * WebP cache (1-year `immutable` TTL on /_next/image, see next.config.ts)
 * re-fetches the new source. Without this, even after a fresh deploy the
 * optimizer keeps serving its baked WebP of the previous PNG.
 *
 * Format: YYYYMMDD or any monotonically-increasing string.
 */
const ASSET_VERSION = "20260522";

/** Resolve a bucket-relative path to a full CDN URL, with a cache-bust query. */
export function asset(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${ASSETS_BASE}${clean}?v=${ASSET_VERSION}`;
}

// ─── Canonical paths ───────────────────────────────────────────────────────────
// Use these constants instead of typing string paths — keeps every app aligned
// when files are renamed/versioned in the bucket.

export const BRAND = {
  logo: {
    /*
     * ONE logo exists in the bucket, and this is it.
     *
     * This map used to advertise seven: primary.svg, primary-white.svg,
     * primary-white.png, primary-black.svg, primary-black.png, wordmark.svg
     * and mark.svg. Every one of them 404s. Nothing on the site referenced
     * them, so nothing was visibly broken — until the dashboard sidebar
     * reached for the white variant on a black background, exactly as a
     * reader of this file would expect to be able to, and rendered a broken
     * image.
     *
     * They are gone rather than commented out. A named export that resolves
     * to a 404 is worse than a missing one: the missing one fails at compile
     * time in front of the person who can fix it.
     *
     * If light/dark variants get uploaded, add them back here — and check
     * they resolve before relying on them.
     */
    /** PNG fallback for email clients that don't render SVG. */
    primaryPng: asset("/logos/primary.png"),
  },
  favicon: {
    ico: asset("/favicons/favicon.ico"),
    png32: asset("/favicons/favicon-32.png"),
    appleTouch: asset("/favicons/apple-touch-icon.png"),
  },
  og: {
    default: asset("/favicons/og-image.png"),
  },
} as const;
